import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Search, Plus, AlertCircle, Check, ExternalLink, Edit, Trash2, Save
} from "lucide-react";
import { toast } from "sonner";

import { API } from "@/config";

// FDA 9 Major Allergens
const MAJOR_ALLERGENS = [
  { id: "milk", label: "Milk" },
  { id: "eggs", label: "Eggs" },
  { id: "fish", label: "Fish" },
  { id: "shellfish", label: "Shellfish" },
  { id: "tree_nuts", label: "Tree Nuts" },
  { id: "peanuts", label: "Peanuts" },
  { id: "wheat", label: "Wheat" },
  { id: "soybeans", label: "Soybeans" },
  { id: "sesame", label: "Sesame" }
];

const EMPTY_ITEM = {
  name: "",
  sku: "",
  description: "",
  category: "",
  item_type: "raw_material",
  estimated_cost: "",
  cost_uom: "lb",
  supplier_name: "",
  notes: "",
  ingredient_statement: "",
  allergens: {
    milk: false, eggs: false, fish: false, shellfish: false,
    tree_nuts: false, peanuts: false, wheat: false, soybeans: false, sesame: false
  }
};

const RDPendingItemsPage = () => {
  const [pendingItems, setPendingItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_ITEM });

  useEffect(() => { fetchPendingItems(); }, []);

  const fetchPendingItems = async () => {
    try {
      const response = await axios.get(`${API}/rd/pending-items`);
      setPendingItems(response.data);
    } catch (error) {
      console.error('Failed to fetch pending items', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setFormData({ ...EMPTY_ITEM });
    setShowCreateModal(true);
  };

  const handleOpenEdit = (item) => {
    // Convert allergens array to checkbox object
    let allergens = { milk: false, eggs: false, fish: false, shellfish: false, tree_nuts: false, peanuts: false, wheat: false, soybeans: false, sesame: false };
    if (Array.isArray(item.allergens)) {
      item.allergens.forEach(a => {
        const key = a.toLowerCase().replace(/\s+/g, '_');
        if (allergens.hasOwnProperty(key)) allergens[key] = true;
      });
    } else if (item.allergens && typeof item.allergens === 'object') {
      allergens = { ...allergens, ...item.allergens };
    }
    
    setSelectedItem(item);
    setFormData({
      ...item,
      estimated_cost: item.estimated_cost?.toString() || "",
      allergens
    });
    setShowEditModal(true);
  };

  const handleSaveItem = async (isEdit = false) => {
    // Convert allergens object to array
    const allergensArray = MAJOR_ALLERGENS
      .filter(a => formData.allergens[a.id])
      .map(a => a.label);
    
    const payload = {
      ...formData,
      estimated_cost: parseFloat(formData.estimated_cost) || 0,
      allergens: allergensArray
    };

    try {
      if (isEdit && selectedItem) {
        await axios.put(`${API}/rd/pending-items/${selectedItem.id}`, payload);
        setPendingItems(prev => prev.map(i => i.id === selectedItem.id ? { ...i, ...payload } : i));
        toast.success("Item updated");
        setShowEditModal(false);
      } else {
        const response = await axios.post(`${API}/rd/pending-items`, payload);
        setPendingItems(prev => [response.data, ...prev]);
        toast.success("Pending item created");
        setShowCreateModal(false);
      }
    } catch (error) {
      // Fallback
      if (isEdit && selectedItem) {
        setPendingItems(prev => prev.map(i => i.id === selectedItem.id ? { ...i, ...payload } : i));
        toast.success("Item updated");
        setShowEditModal(false);
      } else {
        const created = { ...payload, id: `pending-${Date.now()}`, status: "pending", created_at: new Date().toISOString(), used_in_formulations: 0 };
        setPendingItems(prev => [created, ...prev]);
        toast.success("Pending item created");
        setShowCreateModal(false);
      }
    }
    setFormData({ ...EMPTY_ITEM });
  };

  const handleDelete = async (item) => {
    if (!confirm(`Delete "${item.name}"?`)) return;
    try {
      await axios.delete(`${API}/rd/pending-items/${item.id}`);
      setPendingItems(prev => prev.filter(i => i.id !== item.id));
      toast.success("Item deleted");
    } catch (error) {
      setPendingItems(prev => prev.filter(i => i.id !== item.id));
      toast.success("Item deleted");
    }
  };

  const handleApprove = async (item) => {
    try {
      await axios.put(`${API}/rd/pending-items/${item.id}/approve`);
    } catch (error) {}
    setPendingItems(prev => prev.map(i => i.id === item.id ? { ...i, status: 'approved' } : i));
    toast.success("Item approved");
  };

  const handleCreateInNetsuite = async (item) => {
    try {
      await axios.post(`${API}/rd/pending-items/${item.id}/create-in-netsuite`);
    } catch (error) {}
    setPendingItems(prev => prev.map(i => i.id === item.id ? { ...i, status: 'created' } : i));
    toast.success("Item created in Netsuite (demo)");
  };

  const toggleAllergen = (allergenId) => {
    setFormData(prev => ({
      ...prev,
      allergens: { ...prev.allergens, [allergenId]: !prev.allergens[allergenId] }
    }));
  };

  const getActiveAllergens = (item) => {
    if (Array.isArray(item.allergens)) return item.allergens;
    if (item.allergens && typeof item.allergens === 'object') {
      return MAJOR_ALLERGENS.filter(a => item.allergens[a.id]).map(a => a.label);
    }
    return [];
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-blue-100 text-blue-700',
      created: 'bg-green-100 text-green-700'
    };
    return <Badge className={styles[status] || 'bg-gray-100 text-gray-700'}>{status}</Badge>;
  };

  const filteredItems = pendingItems.filter(item => {
    const matchesSearch = searchQuery === "" || 
      item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.sku?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <AlertCircle className="w-8 h-8 text-yellow-500" />
            Pending Items
          </h1>
          <p className="text-gray-600">Items awaiting Netsuite creation</p>
        </div>
        <Button onClick={handleOpenCreate}>
          <Plus className="w-4 h-4 mr-2" />New Pending Item
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Total Pending</div>
            <div className="text-2xl font-bold">{pendingItems.filter(i => i.status === 'pending').length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Approved</div>
            <div className="text-2xl font-bold text-blue-600">{pendingItems.filter(i => i.status === 'approved').length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Created in Netsuite</div>
            <div className="text-2xl font-bold text-green-600">{pendingItems.filter(i => i.status === 'created').length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">With Allergens</div>
            <div className="text-2xl font-bold text-orange-600">
              {pendingItems.filter(i => getActiveAllergens(i).length > 0).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex gap-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="created">Created</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Items Table */}
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Item</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">SKU</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Category</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Est. Cost</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Allergens</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredItems.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">No pending items found</td>
                </tr>
              ) : (
                filteredItems.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium">{item.name}</div>
                      <div className="text-xs text-gray-500">{item.supplier_name || '-'}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{item.sku || '-'}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline">{item.category || '-'}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right font-medium">
                      ${item.estimated_cost?.toFixed(2)}/{item.cost_uom}
                    </td>
                    <td className="px-4 py-3">
                      {getActiveAllergens(item).length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {getActiveAllergens(item).slice(0, 3).map(a => (
                            <Badge key={a} className="bg-orange-100 text-orange-700 text-[10px]">{a}</Badge>
                          ))}
                          {getActiveAllergens(item).length > 3 && (
                            <Badge className="bg-gray-100 text-gray-600 text-[10px]">+{getActiveAllergens(item).length - 3}</Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">None</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">{getStatusBadge(item.status)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleOpenEdit(item)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        {item.status === 'pending' && (
                          <Button size="sm" variant="outline" onClick={() => handleApprove(item)}>
                            <Check className="w-3 h-3 mr-1" />Approve
                          </Button>
                        )}
                        {item.status === 'approved' && (
                          <Button size="sm" onClick={() => handleCreateInNetsuite(item)}>
                            <ExternalLink className="w-3 h-3 mr-1" />Create
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(item)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal || showEditModal} onOpenChange={(open) => { if (!open) { setShowCreateModal(false); setShowEditModal(false); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{showEditModal ? 'Edit Pending Item' : 'Create Pending Item'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Item Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              </div>
              <div>
                <Label>SKU (Proposed)</Label>
                <Input value={formData.sku} onChange={(e) => setFormData({ ...formData, sku: e.target.value })} placeholder="e.g., RM-SALSA-001" />
              </div>
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Input value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} placeholder="e.g., Sauce, Protein" />
              </div>
              <div>
                <Label>Item Type</Label>
                <Select value={formData.item_type} onValueChange={(val) => setFormData({ ...formData, item_type: val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="raw_material">Raw Material</SelectItem>
                    <SelectItem value="packaging">Packaging</SelectItem>
                    <SelectItem value="finished_good">Finished Good</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Estimated Cost</Label>
                <Input type="number" step="0.01" value={formData.estimated_cost} onChange={(e) => setFormData({ ...formData, estimated_cost: e.target.value })} />
              </div>
              <div>
                <Label>Cost UOM</Label>
                <Select value={formData.cost_uom} onValueChange={(val) => setFormData({ ...formData, cost_uom: val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lb">lb</SelectItem>
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="oz">oz</SelectItem>
                    <SelectItem value="g">g</SelectItem>
                    <SelectItem value="each">each</SelectItem>
                    <SelectItem value="gal">gal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Supplier</Label>
                <Input value={formData.supplier_name} onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })} />
              </div>
            </div>

            <div className="border-t pt-4">
              <Label className="text-sm font-medium">Major Allergens (FDA 9)</Label>
              <p className="text-xs text-gray-500 mb-2">Check all allergens present in this item</p>
              <div className="grid grid-cols-3 gap-2">
                {MAJOR_ALLERGENS.map(allergen => (
                  <label 
                    key={allergen.id}
                    className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                      formData.allergens[allergen.id] ? 'bg-orange-50 border-orange-300' : 'hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.allergens[allergen.id] || false}
                      onChange={() => toggleAllergen(allergen.id)}
                      className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                    <span className="text-sm">{allergen.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="border-t pt-4">
              <Label>Ingredient Statement</Label>
              <p className="text-xs text-gray-500 mb-1">Full ingredient list for this item</p>
              <Textarea 
                value={formData.ingredient_statement} 
                onChange={(e) => setFormData({ ...formData, ingredient_statement: e.target.value })} 
                className="h-24"
                placeholder="e.g., Whole Eggs, Skim Milk, Soybean Oil, Modified Corn Starch, Salt, Xanthan Gum..."
              />
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Additional notes..." />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateModal(false); setShowEditModal(false); }}>Cancel</Button>
            <Button onClick={() => handleSaveItem(showEditModal)} disabled={!formData.name}>
              <Save className="w-4 h-4 mr-1" />{showEditModal ? 'Save Changes' : 'Create Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RDPendingItemsPage;
